import { SettingsPermissions } from "@redotech/account-common/permissions";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import {
  RETURNS_AUTOMATIONS_PROCESSING_SEGMENT,
  RETURNS_AUTOMATIONS_SETTINGS_PATH,
  RETURNS_COMPENSATION_FLOW_EDITOR_SEGMENT,
  RETURNS_RESTOCKING_FLOW_EDITOR_SEGMENT,
} from "@redotech/redo-settings-common/flat-team-settings/returns/settings-paths";
import { Navigate, useLocation, useParams } from "react-router-dom";
import {
  catalogRoutePermission,
  INTEGRATIONS_HUB_PERMISSIONS,
} from "../../../../integrations/catalog/catalog-data";
import { LegacyIntegrationVendorRedirect } from "../legacy-integration-vendor-redirect";

const UnifiedIntegrationsVendorRedirect = () => {
  const { slug } = useParams();
  const { search } = useLocation();
  return <Navigate replace to={`../../integrations/vendor/${slug}${search}`} />;
};

export const returnSettingsRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      breadcrumb: "Assessments",
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "assessments",
    lazy: async () => {
      const { AssessmentsWithTabsPage } =
        await import("../../../../setting/assessments/assessments-with-tabs");
      return { Component: AssessmentsWithTabsPage };
    },
  },
  {
    handle: {
      breadcrumb: "Automations",
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: RETURNS_AUTOMATIONS_SETTINGS_PATH,
    children: [
      {
        path: "",
        lazy: async () => {
          const { AutomationsPage } =
            await import("../../../../setting/automation");
          return { Component: AutomationsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Processing",
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: RETURNS_AUTOMATIONS_PROCESSING_SEGMENT,
        children: [
          {
            index: true,
            lazy: async () => {
              const { ProcessingPage } =
                await import("../../../../setting/automation/processing/processing-page");
              return { Component: ProcessingPage };
            },
          },
          {
            handle: {
              breadcrumb: "Compensation flow",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: RETURNS_COMPENSATION_FLOW_EDITOR_SEGMENT,
            lazy: async () => {
              const { CompensationFlowEditor } =
                await import("../../../../setting/flow-editor/compensation-flow/compensation-flow-editor");
              return { Component: CompensationFlowEditor };
            },
          },
          {
            handle: {
              breadcrumb: "Restocking flow",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: RETURNS_RESTOCKING_FLOW_EDITOR_SEGMENT,
            lazy: async () => {
              const { RestockingFlowEditor } =
                await import("../../../../setting/flow-editor/restocking-flow/restocking-flow-editor");
              return { Component: RestockingFlowEditor };
            },
          },
          {
            handle: {
              breadcrumb: "Return processing flows",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: ":flowId",
            lazy: async () => {
              const { ReturnProcessingFlowBuilder } =
                await import("../../../../setting/return-processing-flows/return-processing-flow-builder");
              return { Component: ReturnProcessingFlowBuilder };
            },
          },
        ],
      },
    ],
  },
  {
    handle: {
      breadcrumb: "Consolidation",
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "consolidation",
    lazy: async () => {
      const { ConsolidationSettingsPage } =
        // redont-allow-dynamic-import: route-level code splitting
        await import("@redotech/redo-merchant-fulfillment-app/setting/consolidation");
      return { Component: ConsolidationSettingsPage };
    },
  },
  {
    handle: {
      breadcrumb: "Coverage",
      breadcrumbNavigate: false,
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "coverage",
    children: [
      {
        index: true,
        lazy: async () => {
          const { CoveragePage: CoverageSettings } =
            await import("../../../../setting/coverage/coverage");
          return { Component: CoverageSettings };
        },
      },
      {
        handle: { breadcrumb: "Coverage Builder" },
        path: "coverage-builder",
        lazy: async () => {
          const { CoverageBuilderPage } =
            await import("../../../../setting/coverage/coverage-builder/coverage-builder");
          return { Component: CoverageBuilderPage };
        },
      },
      {
        handle: { breadcrumb: "Custom Logo" },
        path: "custom-logo",
        lazy: async () => {
          const { CustomLogoPage } =
            await import("../../../../setting/coverage/custom-logo");
          return { Component: CustomLogoPage };
        },
      },
      {
        handle: { breadcrumb: "Info Modal" },
        path: "info-modal",
        lazy: async () => {
          const { InfoModalPage } =
            await import("../../../../setting/coverage/info-modal");
          return { Component: InfoModalPage };
        },
      },
    ],
  },
  {
    path: "exchanges/guidance",
    element: <Navigate replace to="../customer-portal/guidance" />,
  },
  {
    handle: {
      breadcrumb: "Exchanges",
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "exchanges",
    lazy: async () => {
      const { ExchangesPage: Exchanges } =
        await import("../../../../setting/exchanges");
      return { Component: Exchanges };
    },
  },
  {
    handle: {
      breadcrumb: "Integrations",
      permission: [...INTEGRATIONS_HUB_PERMISSIONS],
    },
    path: "unified-integrations",
    children: [
      { index: true, element: <Navigate replace to="../integrations" /> },
      { path: "vendor/:slug", element: <UnifiedIntegrationsVendorRedirect /> },
    ],
  },
  {
    handle: {
      breadcrumb: "Integrations",
      permission: [...INTEGRATIONS_HUB_PERMISSIONS],
    },
    path: "integrations",
    children: [
      {
        path: "",
        lazy: async () => {
          const { UnifiedIntegrationsPage } =
            await import("../../../../integrations/pages/unified-integrations-page");
          return { Component: UnifiedIntegrationsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Cordial",
          permission: catalogRoutePermission("cordial"),
        },
        path: "cordial",
        lazy: async () => {
          const { CordialIntegrationSettingsPage } =
            await import("../../../../setting/cordial-integration-settings");
          return { Component: CordialIntegrationSettingsPage };
        },
      },
      {
        path: "comment-sold",
        element: <LegacyIntegrationVendorRedirect slug="comment-sold" />,
      },
      {
        path: "netsuite",
        element: <LegacyIntegrationVendorRedirect slug="netsuite" />,
      },
      {
        path: "indigo8",
        element: <LegacyIntegrationVendorRedirect slug="indigo8" />,
      },
      {
        path: "odoo",
        element: <LegacyIntegrationVendorRedirect slug="odoo" />,
      },
      {
        path: "shipmonk",
        element: <LegacyIntegrationVendorRedirect slug="shipmonk" />,
      },
      {
        path: "masonhub",
        element: <LegacyIntegrationVendorRedirect slug="masonhub" />,
      },
      {
        path: "trackstar",
        element: <LegacyIntegrationVendorRedirect slug="trackstar" />,
      },
      {
        path: "aims360",
        element: <LegacyIntegrationVendorRedirect slug="aims360" />,
      },
      {
        path: "apparel-magic",
        element: <LegacyIntegrationVendorRedirect slug="apparel-magic" />,
      },
      {
        path: "authorizenet",
        element: <LegacyIntegrationVendorRedirect slug="authorizenet" />,
      },
      {
        path: "blue-cherry",
        element: <LegacyIntegrationVendorRedirect slug="blue-cherry" />,
      },
      {
        path: "controlport",
        element: <LegacyIntegrationVendorRedirect slug="controlport" />,
      },
      {
        path: "mintsoft",
        element: <LegacyIntegrationVendorRedirect slug="mintsoft" />,
      },
      {
        path: "blue-yonder",
        element: <LegacyIntegrationVendorRedirect slug="blue-yonder" />,
      },
      {
        path: "brightpearl",
        element: <LegacyIntegrationVendorRedirect slug="brightpearl" />,
      },
      {
        path: "cloudx",
        element: <LegacyIntegrationVendorRedirect slug="cloudx" />,
      },
      {
        path: "comment-sold/manage",
        element: <LegacyIntegrationVendorRedirect slug="comment-sold" />,
      },
      {
        path: "bubblehouse",
        element: <LegacyIntegrationVendorRedirect slug="bubblehouse" />,
      },
      {
        path: "creditsyard",
        element: <LegacyIntegrationVendorRedirect slug="creditsyard" />,
      },
      {
        path: "custom-return",
        element: <LegacyIntegrationVendorRedirect slug="custom-return" />,
      },
      {
        path: "aptos",
        element: <LegacyIntegrationVendorRedirect slug="aptos" />,
      },
      {
        path: "deposco",
        element: <LegacyIntegrationVendorRedirect slug="deposco" />,
      },
      {
        path: "extensiv",
        element: <LegacyIntegrationVendorRedirect slug="extensiv" />,
      },
      {
        path: "flexport",
        element: <LegacyIntegrationVendorRedirect slug="flexport" />,
      },
      {
        path: "globale",
        element: <LegacyIntegrationVendorRedirect slug="globale" />,
      },
      {
        path: "idf-shipping",
        element: <LegacyIntegrationVendorRedirect slug="idf-shipping" />,
      },
      {
        path: "ecwid",
        element: <LegacyIntegrationVendorRedirect slug="ecwid" />,
      },
      {
        path: "inmar",
        element: <LegacyIntegrationVendorRedirect slug="inmar" />,
      },
      {
        path: "katana",
        element: <LegacyIntegrationVendorRedirect slug="katana" />,
      },
      {
        handle: {
          breadcrumb: "Integration",
          permission: [...INTEGRATIONS_HUB_PERMISSIONS],
        },
        path: "vendor/:slug",
        lazy: async () => {
          const { IntegrationVendorPage } =
            await import("../../../../integrations/pages/integration-vendor-page");
          return { Component: IntegrationVendorPage };
        },
      },
      {
        path: "klaviyo",
        element: <LegacyIntegrationVendorRedirect slug="klaviyo" />,
      },
      {
        path: "loop",
        element: <LegacyIntegrationVendorRedirect slug="loop" />,
      },
      {
        path: "narvar",
        element: <LegacyIntegrationVendorRedirect slug="narvar" />,
      },
      {
        path: "return-logic",
        element: <LegacyIntegrationVendorRedirect slug="return-logic" />,
      },
      {
        path: "luminous",
        element: <LegacyIntegrationVendorRedirect slug="luminous" />,
      },
      {
        path: "purple-dot",
        element: <LegacyIntegrationVendorRedirect slug="purple-dot" />,
      },
      {
        path: "refundid",
        element: <LegacyIntegrationVendorRedirect slug="refundid" />,
      },
      {
        path: "rise-ai",
        element: <LegacyIntegrationVendorRedirect slug="rise-ai" />,
      },
      {
        path: "loopz",
        element: <LegacyIntegrationVendorRedirect slug="loopz" />,
      },
      {
        path: "rivo",
        element: <LegacyIntegrationVendorRedirect slug="rivo" />,
      },
      {
        path: "seko",
        element: <LegacyIntegrationVendorRedirect slug="seko" />,
      },
      {
        path: "shipbob",
        element: <LegacyIntegrationVendorRedirect slug="shipbob" />,
      },
      {
        path: "shipedge",
        element: <LegacyIntegrationVendorRedirect slug="shipedge" />,
      },
      {
        path: "shiphero",
        element: <LegacyIntegrationVendorRedirect slug="shiphero" />,
      },
      {
        path: "ryder",
        element: <LegacyIntegrationVendorRedirect slug="ryder" />,
      },
      {
        path: "cin7-omni",
        element: <LegacyIntegrationVendorRedirect slug="cin7-omni" />,
      },
      {
        path: "teamwork-commerce",
        element: <LegacyIntegrationVendorRedirect slug="teamwork-commerce" />,
      },
      {
        path: "tiktok-shop",
        element: <LegacyIntegrationVendorRedirect slug="tiktok-shop" />,
      },
    ],
  },
  {
    handle: {
      breadcrumb: "Merchant-created returns",
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "permissions",
    lazy: async () => {
      const { PermissionsPage } =
        await import("../../../../setting/permissions/permissions");
      return { Component: PermissionsPage };
    },
  },
  {
    handle: {
      breadcrumb: "Point of sale",
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "pos",
    children: [
      {
        path: "",
        lazy: async () => {
          const { PointOfSalePage } =
            await import("../../../../setting/point-of-sale/point-of-sale-page");
          return { Component: PointOfSalePage };
        },
      },
      {
        handle: {
          breadcrumb: "Integration locations",
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "integration-locations",
        lazy: async () => {
          const { IntegrationLocationsPage } =
            await import("../../../../setting/point-of-sale/integration-locations-page");
          return { Component: IntegrationLocationsPage };
        },
      },
    ],
  },
  {
    handle: {
      breadcrumb: "Post processing",
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "post-processing",
    lazy: async () => {
      const { PostProcessingPage } =
        await import("@redotech/redo-merchant-fulfillment-app/setting/post-processing");
      return { Component: PostProcessingPage };
    },
  },
  {
    handle: {
      breadcrumb: "Processing groups",
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "processing-groups",
    children: [
      {
        path: "",
        lazy: async () => {
          const { ProcessingGroupsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/processing-groups");
          return { Component: ProcessingGroupsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Group details",
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: ":groupId",
        lazy: async () => {
          const { ProcessingGroupDetailPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/processing-group-detail");
          return { Component: ProcessingGroupDetailPage };
        },
      },
    ],
  },
  {
    handle: {
      breadcrumb: "Refunds & store credit",
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "processing",
    lazy: async () => {
      const { ProcessingPage } = await import("../../../../setting/processing");
      return { Component: ProcessingPage };
    },
  },
  {
    handle: {
      breadcrumb: "Return detail view",
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "returns-ui",
    lazy: async () => {
      const { ReturnsUiPage } =
        await import("../../../../setting/returns-ui/returns-ui");
      return { element: <ReturnsUiPage /> };
    },
  },
  {
    path: "sort-destinations",
    element: <Navigate replace to="../post-processing" />,
  },
  {
    handle: {
      breadcrumb: "Shipping",
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "shipping",
    children: [
      {
        path: "",
        lazy: async () => {
          const { ShippingPage } =
            await import("../../../../setting/shipping/shipping-page");
          return { Component: ShippingPage };
        },
      },
      {
        handle: {
          breadcrumb: "Label flow",
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "labels-flow-editor",
        lazy: async () => {
          const { LabelsFlowEditor } =
            await import("../../../../setting/flows/labels-flow-editor");
          return { Component: LabelsFlowEditor };
        },
      },
      {
        handle: {
          breadcrumb: "Location flow",
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "location-flow-editor",
        lazy: async () => {
          const { LocationFlowEditor } =
            await import("../../../../setting/flows/location-flow-editor");
          return { Component: LocationFlowEditor };
        },
      },
    ],
  },
  {
    handle: {
      breadcrumb: "Warehouse",
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: "warehouse",
    children: [
      {
        path: "",
        lazy: async () => {
          const { ReturnsCentersIndexPage } =
            await import("../../../../setting/managed-returns/returns-centers-index");
          return { Component: ReturnsCentersIndexPage };
        },
      },
      {
        handle: {
          breadcrumb: "Integration",
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "integrations/:provider",
        children: [
          {
            path: "",
            lazy: async () => {
              const { IntegrationDetailPage } =
                await import("../../../../setting/managed-returns/integration-detail");
              return { Component: IntegrationDetailPage };
            },
          },
          {
            handle: {
              breadcrumb: "Product mappings",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "product-mappings",
            lazy: async () => {
              const { ProductMappingsPage } =
                await import("../../../../setting/managed-returns/product-mappings");
              return { Component: ProductMappingsPage };
            },
          },
        ],
      },
      {
        handle: {
          breadcrumb: "Photo guidance",
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "photo-guidance",
        lazy: async () => {
          const { PhotoInstructionsPage } =
            // redont-allow-dynamic-import: route-level code splitting
            await import("@redotech/redo-merchant-fulfillment-app/setting/photo-instructions/photo-instructions-page");
          return { Component: PhotoInstructionsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Processing flow",
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "processing-flow",
        lazy: async () => {
          const { WarehouseProcessingFlowEditor } =
            await import("../../../../setting/flows/warehouse-processing-flow-editor");
          return { element: <WarehouseProcessingFlowEditor /> };
        },
      },
      {
        handle: {
          breadcrumb: "Product transform",
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "product-transform",
        lazy: async () => {
          const { ProductTransformPage } =
            await import("../../../../setting/product/product-transform");
          return { Component: ProductTransformPage };
        },
      },
    ],
  },
  {
    handle: {
      breadcrumb: "Withdrawals",
      permission: [
        SettingsPermissions.SET_GENERAL_SETTINGS,
        SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      ],
    },
    path: "withdrawals",
    lazy: async () => {
      const { WithdrawalsPage } =
        await import("@redotech/redo-merchant-general-app/setting/withdrawals/withdrawals-page");
      return { Component: WithdrawalsPage };
    },
  },
];
